import { useState } from 'react';
import { useQuery } from 'react-query';
import { X, Loader2, Calendar, Droplet } from 'lucide-react';
import { serviceCallsAPI } from '@shared/apiClient';
import toast from 'react-hot-toast';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MonthlyFuelFiltersModal = ({ isOpen, onClose }) => {
  const currentMonth = new Date().getMonth() + 1; // JavaScript months are 0-based
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  // Fetch fuel filters for the selected month
  const { data, isLoading, error } = useQuery(
    ['monthlyFuelFilters', selectedMonth],
    () => serviceCallsAPI.getMonthlyFuelFilters(selectedMonth),
    { 
      enabled: isOpen && !!selectedMonth,
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to fetch fuel filters');
      }
    }
  );

  if (!isOpen) return null;

  const fuelFilterData = data?.data;
  const filters = fuelFilterData?.filters || [];
  const details = fuelFilterData?.details || [];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-md bg-white mb-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Droplet className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Monthly Fuel Filter Requirements
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                View fuel filters needed for contract clients scheduled in a specific month
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Month Selector */}
        <div className="mb-6 bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="h-4 w-4 inline mr-2" />
            Select Month
          </label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
          >
            {MONTH_NAMES.map((monthName, index) => (
              <option key={index + 1} value={index + 1}>
                {monthName}
              </option>
            ))}
          </select>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-orange-600 animate-spin" />
            <span className="ml-3 text-gray-600">Loading fuel filter requirements...</span>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <p className="text-sm text-red-800">
              Failed to load fuel filter data. Please try again.
            </p>
          </div>
        )}

        {/* Content */}
        {!isLoading && !error && fuelFilterData && (
          <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="text-sm font-medium text-orange-700">Total Clients</div>
                <div className="text-2xl font-bold text-orange-900 mt-1">
                  {fuelFilterData.totalClients}
                </div>
                <div className="text-xs text-orange-600 mt-1">
                  Scheduled for {MONTH_NAMES[selectedMonth - 1]}
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm font-medium text-blue-700">Clients Needing Fuel Filters</div>
                <div className="text-2xl font-bold text-blue-900 mt-1">
                  {fuelFilterData.clientsNeedingFuelFilters}
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  Have fuel filter info
                </div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="text-sm font-medium text-purple-700">Unique Filters</div>
                <div className="text-2xl font-bold text-purple-900 mt-1">
                  {filters.length}
                </div>
                <div className="text-xs text-purple-600 mt-1">
                  Different filter types
                </div>
              </div>
            </div>

            {/* No Data State */}
            {filters.length === 0 && (
              <div className="text-center py-12">
                <Droplet className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">
                  No fuel filters needed for {MONTH_NAMES[selectedMonth - 1]}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  No clients are scheduled with fuel filter information this month.
                </p>
              </div>
            )}

            {/* Filter Summary Table */}
            {filters.length > 0 && (
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">
                  Fuel Filter Summary
                </h4>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Filter Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity Needed
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Clients
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filters.map((filter, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Droplet className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">
                                {filter.filterNumber}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                              {filter.count}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {filter.clients.map((client, idx) => (
                                <div key={idx} className="mb-1">
                                  <span className="font-medium">{client.clientName}</span>
                                  <span className="text-gray-500 text-xs ml-2">
                                    ({client.filterType})
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Detailed List */}
            {details.length > 0 && (
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">
                  Detailed Customer List
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="space-y-2">
                    {details.map((item, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between bg-white p-3 rounded border border-gray-100"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {item.clientName}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {item.filterType} Fuel Filter
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-orange-600">
                            {item.filterNumber}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonthlyFuelFiltersModal;
